const OPEN_DELAY = 130;
const CLOSE_DELAY = 220;
const ROOT_SELECTOR = 'header-menu[data-yuxin-corporate-nav]';

class YuxinCorporateMenu {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.openItem = null;
    this.pendingItem = null;
    this.openTimer = undefined;
    this.closeTimer = undefined;
    this.pointerReady = false;
    this.lastPointer = null;
    this.abortController = new AbortController();

    this.reset();
    this.bind();
    requestAnimationFrame(() => this.reset());
  }

  bind() {
    const { signal } = this.abortController;

    // Pointer handling is delegated. Shopify/Horizon may replace menu-list
    // descendants during hydration, so listeners attached to individual
    // links can become stale while the header-menu element remains mounted.
    document.addEventListener('pointermove', this.handleDocumentPointerMove, { capture: true, signal });
    this.root.addEventListener('pointerover', this.handlePointerOver, { signal });
    this.root.addEventListener('pointerout', this.handlePointerOut, { signal });
    window.addEventListener('pageshow', this.reset, { signal });
    window.addEventListener('resize', this.updateMenuTop, { passive: true, signal });
    window.addEventListener('scroll', this.closeImmediately, { passive: true, signal });
    this.root.addEventListener('keydown', this.handleKeydown, { signal });

    this.root.addEventListener('focusin', this.handleFocusIn, { signal });
  }

  handleDocumentPointerMove = (event) => {
    if (!event.isTrusted || event.pointerType === 'touch') return;

    const point = { x: event.clientX, y: event.clientY };
    if (!this.lastPointer || point.x !== this.lastPointer.x || point.y !== this.lastPointer.y) this.pointerReady = true;
    this.lastPointer = point;

    if (!this.pointerReady) return;
    this.root.classList.add('is-pointer-ready');
    const item = event.target instanceof Element ? event.target.closest('[data-yuxin-menu-item]') : null;
    if (item && this.root.contains(item) && item !== this.openItem) this.scheduleOpen(item);
  };

  handlePointerOver = (event) => {
    if (!this.pointerReady || event.pointerType === 'touch' || !(event.target instanceof Element)) return;

    const tab = event.target.closest('[data-yuxin-tab]');
    if (tab && this.root.contains(tab)) {
      this.activateTab(tab);
      this.cancelClose();
      return;
    }

    const item = event.target.closest('[data-yuxin-menu-item]');
    if (item && this.root.contains(item)) this.scheduleOpen(item);
  };

  handlePointerOut = (event) => {
    if (!(event.target instanceof Element)) return;
    const next = event.relatedTarget;
    if (next instanceof Node && this.root.contains(next)) return;
    clearTimeout(this.openTimer);
    this.openTimer = undefined;
    this.pendingItem = null;
    this.scheduleClose();
  };

  handleFocusIn = (event) => {
    if (!(event.target instanceof Element)) return;
    const tab = event.target.closest('[data-yuxin-tab]');
    if (tab) this.activateTab(tab);
  };

  activateTab(tab) {
    const megaMenu = tab.closest('[data-yuxin-mega-menu]');
    if (!megaMenu) return;
    const index = tab.dataset.tabIndex;
    if (index == null) return;

    megaMenu.querySelectorAll('[data-yuxin-tab]').forEach((candidate) => {
      const isActive = candidate === tab;
      candidate.classList.toggle('is-active', isActive);
      candidate.setAttribute('aria-selected', String(isActive));
    });
    megaMenu.querySelectorAll('[data-yuxin-panel]').forEach((panel) => {
      const isActive = panel.dataset.panelIndex === index;
      panel.classList.toggle('is-active', isActive);
      panel.setAttribute('aria-hidden', String(!isActive));
    });
  }

  /** @param {HTMLElement} item */
  scheduleOpen(item) {
    const submenu = item.querySelector(':scope > [data-yuxin-submenu]');
    clearTimeout(this.closeTimer);
    clearTimeout(this.openTimer);
    this.closeTimer = undefined;

    if (!submenu) {
      this.scheduleClose();
      return;
    }

    if (item === this.openItem) return;
    if (item === this.pendingItem && this.openTimer) return;
    this.pendingItem = item;
    this.openTimer = setTimeout(() => {
      this.pendingItem = null;
      if (item.isConnected && item.matches(':hover')) this.open(item);
    }, OPEN_DELAY);
  }

  /** @param {HTMLElement} item */
  open(item) {
    if (item === this.openItem) return;
    this.pendingItem = null;
    this.closeItem(this.openItem);
    this.updateMenuTop();

    const trigger = item.querySelector(':scope > [data-yuxin-trigger]');
    const submenu = item.querySelector(':scope > [data-yuxin-submenu]');
    if (!trigger || !submenu) return;

    this.openItem = item;
    item.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    submenu.setAttribute('aria-hidden', 'false');
    this.root.classList.add('has-open-menu');
  }

  scheduleClose = () => {
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => {
      if (!this.openItem?.matches(':hover')) this.closeImmediately();
    }, CLOSE_DELAY);
  };

  cancelClose = () => {
    clearTimeout(this.closeTimer);
    this.closeTimer = undefined;
  };

  closeImmediately = () => {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    this.openTimer = undefined;
    this.closeTimer = undefined;
    this.pendingItem = null;
    this.closeItem(this.openItem);
    this.openItem = null;
    this.root.classList.remove('has-open-menu');
    this.root.classList.remove('is-pointer-ready');
  };

  /** @param {HTMLElement | null} item */
  closeItem(item) {
    if (!item) return;
    item.classList.remove('is-open');
    item.querySelector(':scope > [data-yuxin-trigger]')?.setAttribute('aria-expanded', 'false');
    item.querySelector(':scope > [data-yuxin-submenu]')?.setAttribute('aria-hidden', 'true');
  }

  handleKeydown = (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-yuxin-trigger]') : null;

    if (event.key === 'Escape') {
      const previousTrigger = this.openItem?.querySelector('[data-yuxin-trigger]');
      this.closeImmediately();
      previousTrigger?.focus();
      return;
    }

    if (!trigger || !['ArrowDown', ' '].includes(event.key)) return;
    const item = trigger.closest('[data-yuxin-menu-item]');
    if (!item?.querySelector('[data-yuxin-submenu]')) return;

    event.preventDefault();
    this.open(item);
    item.querySelector('[data-yuxin-tab]')?.focus();
  };

  updateMenuTop = () => {
    const header = this.root.closest('header-component');
    const bottom = header?.getBoundingClientRect().bottom ?? this.root.getBoundingClientRect().bottom;
    this.root.style.setProperty('--yuxin-menu-top', `${Math.max(0, Math.round(bottom))}px`);
  };

  reset = () => {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    this.openTimer = undefined;
    this.closeTimer = undefined;
    this.pendingItem = null;
    this.openItem = null;
    this.root.classList.remove('has-open-menu');
    this.root.classList.remove('is-pointer-ready');

    for (const item of this.root.querySelectorAll('[data-yuxin-menu-item]')) {
      item.classList.remove('is-open');
      item.querySelector(':scope > [data-yuxin-trigger]')?.setAttribute('aria-expanded', 'false');
      item.querySelector(':scope > [data-yuxin-submenu]')?.setAttribute('aria-hidden', 'true');
    }
    this.updateMenuTop();
  };
}

function initializeYuxinMenus(container = document) {
  container.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
    if (root.dataset.yuxinMenuReady === 'true') return;
    root.dataset.yuxinMenuReady = 'true';
    new YuxinCorporateMenu(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeYuxinMenus(), { once: true });
} else {
  initializeYuxinMenus();
}

document.addEventListener('shopify:section:load', (event) => initializeYuxinMenus(event.target));
